import styled from "@emotion/styled"
import { AnimatePresence } from "framer-motion"

import {
    Confetti, MaskHeadline, MsEyebrow, MsName, MsNameMark, MsQuoteWrap, MsRow, Row, Stack,
} from "../ms"
import { paper, fonts, shade } from "../theme"
import { joinUrl } from "../route"
import type {
    GameOverView,
    PlayerInfo,
    ResultsView,
    RoundLeaderboardView,
    SelectArticleView,
    VotingResultsView,
    VotingView,
    WordPickerView,
} from "../protocol"
import { TvStageArea } from "./TvStage"
import {
    TvAvatar, TvChip, TvDeck, TvDisplay, TvDots, TvEyebrow, TvPaper, TvPlate,
    TvQmark, TvQr, TvQtext, TvRuleRed, TvStamp, TvTape,
} from "./tvType"

/**
 * The TV screens.
 *
 * Each is a pure projection of one snapshot, the same as its phone counterpart —
 * and deliberately says the same things in the same words. Where the phone says
 * "Pick the best misquote." so does the television; where it labels a count
 * "Awaiting · 3", so does this. The prototype these grew from spoke a different
 * language entirely (tonight's wire, the press, at the desk, selections filed),
 * which read as a second product wearing the first one's colours.
 *
 * The rule: if the phone has a word for something, use its word. If the phone
 * doesn't label something, don't label it here either — a caption that only
 * exists to name the obvious is the thing a ten-foot screen can least afford.
 */

const pad2 = (n: number) => String(n).padStart(2, "0")

/** Names the lobby's joining feed shows before it starts counting instead. */
const LOBBY_FEED_MAX = 7

/** The blank set as a rule, the way the phone's prompt screens set it. */
const setPrompt = (text: string) => text.replace(/___/g, "_____")

const seatedOnly = (players: PlayerInfo[]) => players.filter(p => p.role !== "spectator")

const nameOf = (players: PlayerInfo[], id: string) =>
    players.find(p => p.id === id)?.name ?? "—"

/* ── Shared furniture ───────────────────────────────────────────────────── */

const Centered = styled(TvStageArea)`
    flex: 1;
    align-items: center;
    justify-content: center;
    text-align: center;
`

/** The prompt, quoted, sitting above whatever the round is doing with it. */
const PromptStrip = styled.div`
    padding-bottom: 16px;
    border-bottom: 1px dashed ${shade(0.2)};
`

const PromptQuote = styled(TvDisplay)`
    font-style: italic;
    font-weight: 600;
    line-height: 1.16;
`

function Prompt({ text }: { text: string }) {
    return (
        <PromptStrip>
            <PromptQuote size={46}>&ldquo;{setPrompt(text)}&rdquo;</PromptQuote>
        </PromptStrip>
    )
}

/**
 * Who the room is waiting on, said the way the phone says it: a muted count and
 * a row of chips, animated in and out exactly as Voting.tsx animates its own.
 * Replaces four different bespoke "N of M filed" readouts.
 */
function Awaiting({ players, label = "Awaiting" }: { players: PlayerInfo[]; label?: string }) {
    if (players.length === 0) return null
    return (
        <Stack gap={14} align="center" style={{ marginTop: "auto" }}>
            <TvEyebrow tone="mute">{label} · {players.length}</TvEyebrow>
            <Row gap={12} justify="center" wrap>
                <AnimatePresence>
                    {players.map(p => (
                        <TvChip
                            key={p.id}
                            layout
                            initial={{ opacity: 0, scale: 0.85, y: 8 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.85, y: -8 }}
                        >
                            {p.name}
                        </TvChip>
                    ))}
                </AnimatePresence>
            </Row>
        </Stack>
    )
}

/* ══ Lobby ═══════════════════════════════════════════════════════════════ */

const LobbyGrid = styled(TvStageArea)`
    display: grid;
    grid-template-columns: 1fr 680px;
    gap: 90px;
    align-items: center;
    flex: 1;
`

const LobbyLeft = styled.div`
    display: flex;
    flex-direction: column;
`

const CodeDisplay = styled(TvDisplay)`
    line-height: 0.9;
    letter-spacing: -0.04em;
    text-align: center;
`

const JoinUrl = styled(TvDeck)`
    font-style: normal;
    font-weight: 700;
    color: ${paper.ink};
    text-align: center;
    margin-top: 14px;
`

const JoinList = styled(Stack)`
    min-height: 0;
    overflow: clip;
    width: 100%;
`

/** The phone's roster row, grown. Same paper, same hairline, same `you` accent —
 *  here it marks the host, since a shared screen has no "you". */
const JoinRow = styled(MsRow)`
    padding: 14px 20px;
    gap: 18px;
    border-radius: 10px;
`

/** Deliberately no colour of its own: MsRow sets the ink/paper pair for the
 *  whole row, and a name that restates it goes invisible on the dark variant. */
const JoinName = styled(MsName)`
    font-size: 30px;
`

export function TvLobby({ code, players }: { code: string; players: PlayerInfo[] }) {
    // The QR points at the same canonical join link the host would share, built
    // from wherever this page is served — so a TV on a staging host sends phones
    // to staging rather than to production.
    const url = joinUrl(window.location.origin, code)
    const pretty = url.replace(/^https?:\/\//, "")

    return (
        <LobbyGrid>
            <LobbyLeft>
                <TvDisplay size={150} style={{ lineHeight: 0.88, letterSpacing: "-0.04em" }}>
                    <MaskHeadline>Misquote the internet.</MaskHeadline>
                </TvDisplay>
                <TvRuleRed style={{ margin: "28px 0" }} />
                <TvDeck size={30}>
                    Build the funniest answer from a Wikipedia article's words.
                </TvDeck>

                <Stack gap={12} style={{ marginTop: 44, width: "100%" }}>
                    <TvEyebrow tone="mute">Players · {players.length}</TvEyebrow>
                    <JoinList gap={10}>
                        {players.slice(0, LOBBY_FEED_MAX).map(p => (
                            <JoinRow key={p.id} you={p.isHost}>
                                <TvAvatar size={44} you={p.isHost}>{p.name.slice(0, 2)}</TvAvatar>
                                <JoinName>
                                    {p.isHost && <MsNameMark you>★</MsNameMark>}
                                    {p.name}
                                </JoinName>
                                {p.isHost && <TvEyebrow tone="sticky">host</TvEyebrow>}
                            </JoinRow>
                        ))}
                        {players.length > LOBBY_FEED_MAX && (
                            <TvEyebrow tone="mute">
                                + {players.length - LOBBY_FEED_MAX} more
                            </TvEyebrow>
                        )}
                        {players.length === 0 && (
                            <Row gap={16} align="center">
                                <TvDeck size={28}>Waiting for players</TvDeck>
                                <TvDots />
                            </Row>
                        )}
                    </JoinList>
                </Stack>
            </LobbyLeft>

            <TvPaper style={{ padding: "44px 48px 40px" }}>
                <TvTape placement="tl" />
                <TvEyebrow style={{ textAlign: "center" }}>Room code</TvEyebrow>
                <CodeDisplay size={190}>{code}</CodeDisplay>
                <Stack gap={16} align="center" style={{ marginTop: 34 }}>
                    <TvEyebrow tone="red">Scan to join</TvEyebrow>
                    <TvQr value={url} size={250} />
                    <JoinUrl size={24}>{pretty}</JoinUrl>
                </Stack>
            </TvPaper>
        </LobbyGrid>
    )
}

/* ══ Round intro ═════════════════════════════════════════════════════════ */

export function TvRoundIntro({ round, totalRounds, dealer }: {
    round: number
    totalRounds: number | null
    dealer: string | null
}) {
    return (
        <Centered>
            <TvDisplay size={240} style={{ lineHeight: 0.86, letterSpacing: "-0.04em" }}>
                <MaskHeadline>Round {pad2(round)}</MaskHeadline>
            </TvDisplay>
            {totalRounds != null && (
                <TvEyebrow tone="mute" style={{ marginTop: 18 }}>of {totalRounds}</TvEyebrow>
            )}
            <TvRuleRed style={{ margin: "34px 0" }} />
            {dealer && <TvDeck size={34}>{dealer} has the deck.</TvDeck>}
        </Centered>
    )
}

/* ══ Draw ════════════════════════════════════════════════════════════════ */

const DeckStack = styled.div`
    position: relative;
    width: 300px;
    height: 400px;
    margin: 40px auto 34px;
`

/** Three cards mid-shuffle. The face-down card wears the same red the phone's
 *  PromptCardBack does, so the deck reads as the same deck on both screens. */
const DeckCard = styled.div<{ back?: boolean }>`
    position: absolute;
    inset: 0;
    border-radius: 10px;
    background: ${p => p.back
        ? `linear-gradient(180deg, ${paper.redLight} 0%, ${paper.red} 100%)`
        : paper.paperCool};
    box-shadow:
        0 0 0 1px ${shade(0.06)},
        0 30px 60px -20px ${shade(0.5)},
        0 60px 90px -40px ${shade(0.55)};
    animation: tv-shuffle 2.6s cubic-bezier(0.22, 1, 0.36, 1) infinite;

    &:nth-of-type(1) { transform: rotate(-7deg) translate(-10px, 6px); }
    &:nth-of-type(2) { transform: rotate(4deg) translate(10px, -4px); animation-delay: 0.18s; }
    &:nth-of-type(3) { transform: rotate(-1deg); animation-delay: 0.36s; }

    @keyframes tv-shuffle {
        0%, 100% { }
        50% { transform: rotate(2deg) translate(4px, -16px); }
    }
`

const DeckGlyph = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 900;
    font-size: 190px;
    color: ${paper.paper};
    opacity: 0.9;
`

export function TvDraw({ dealer }: { dealer: string | null }) {
    return (
        <Centered>
            <DeckStack>
                <DeckCard />
                <DeckCard />
                <DeckCard back><DeckGlyph>&ldquo;</DeckGlyph></DeckCard>
            </DeckStack>
            <TvDisplay size={104}>
                <MaskHeadline>{dealer ? `${dealer} is drawing.` : "Drawing a card."}</MaskHeadline>
            </TvDisplay>
        </Centered>
    )
}

/* ══ Headline (prompt revealed) ══════════════════════════════════════════ */

const HeadlineCard = styled(TvPaper)`
    padding: 70px 90px 76px;
    max-width: 1480px;
    width: 100%;
    text-align: left;
`

/** The oversized opening mark the phone's quote cards carry, at card scale. */
const HeadlineGlyph = styled.div`
    position: absolute;
    /* Clear of the tape at the card's top-left corner — the mark reads as part
       of the page, not as something stuck under the tape. */
    top: 44px;
    left: 40px;
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 900;
    font-size: 300px;
    line-height: 0.6;
    color: ${paper.red};
    opacity: 0.16;
    pointer-events: none;
    user-select: none;
`

export function TvHeadline({ prompt, dealer }: { prompt: string; dealer: string | null }) {
    return (
        <Centered>
            <HeadlineCard>
                <TvTape placement="tl" />
                <TvTape placement="tr" red />
                <HeadlineGlyph>&ldquo;</HeadlineGlyph>
                <TvDisplay size={94} weight={700} style={{ position: "relative", lineHeight: 1.08 }}>
                    <MaskHeadline delay={0.15}>{setPrompt(prompt)}</MaskHeadline>
                </TvDisplay>
                <TvRuleRed style={{ marginTop: 36 }} />
            </HeadlineCard>
            <TvDeck size={30} style={{ marginTop: 34 }}>
                {dealer ? `Drawn by ${dealer}. ` : ""}Pick your article.
            </TvDeck>
        </Centered>
    )
}

/* ══ Pick your article ═══════════════════════════════════════════════════ */

/**
 * Each player is dealt their own articles and the server never sends them here,
 * so the TV shows the shape of the choosing rather than the choices: one folder
 * per desk, stamped as each is filed.
 */
const Fan = styled.div`
    position: relative;
    height: 290px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-end;
`

const Folder = styled.div<{ picked?: boolean }>`
    position: absolute;
    bottom: 0;
    width: 196px;
    height: 250px;
    padding: 28px 18px 18px;
    border-radius: 4px 4px 8px 8px;
    background: ${p => p.picked ? paper.paper : paper.paperCool};
    box-shadow: ${p => p.picked
        ? `0 0 0 2px ${paper.red}, 0 14px 28px -12px ${shade(0.45)}`
        : `0 0 0 1px ${shade(0.18)}, 0 14px 28px -12px ${shade(0.45)}`};
    transform-origin: bottom center;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: background 0.4s ease, box-shadow 0.4s ease;
`

const FolderTab = styled.div<{ picked?: boolean }>`
    position: absolute;
    top: -13px;
    left: 50%;
    transform: translateX(-50%);
    background: ${p => p.picked ? paper.red : paper.ink};
    color: ${paper.paper};
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 700;
    font-size: 19px;
    padding: 4px 15px;
    border-radius: 5px 5px 0 0;
    white-space: nowrap;
    max-width: 176px;
    overflow: hidden;
    text-overflow: ellipsis;
`

const FolderLines = styled.div`
    display: flex;
    flex-direction: column;
    gap: 11px;
    flex: 1;

    div { height: 6px; background: ${shade(0.18)}; border-radius: 2px; }
    div:nth-of-type(2) { width: 80%; }
    div:nth-of-type(4) { width: 60%; }
    div:nth-of-type(6) { width: 70%; }
`

const FolderStamp = styled(TvStamp)`
    position: absolute;
    bottom: 44px;
    right: 10px;
    font-size: 14px;
    padding: 3px 10px;
    border-width: 2px;
    transform: rotate(-8deg);
`

export function TvSourcePick({ view, players }: { view: SelectArticleView; players: PlayerInfo[] }) {
    const seated = seatedOnly(players)
    const waitingIds = new Set(view.awaitingPlayers.map(p => p.id))

    return (
        <TvStageArea style={{ gap: 26, flex: 1 }}>
            <Prompt text={view.prompt} />

            <Stack gap={20} align="center" style={{ margin: "auto 0" }}>
                <TvDisplay size={78}>
                    <MaskHeadline>Pick your article.</MaskHeadline>
                </TvDisplay>
                <Fan>
                    {seated.map((p, i) => {
                        const offset = i - (seated.length - 1) / 2
                        const picked = !waitingIds.has(p.id)
                        return (
                            <Folder
                                key={p.id}
                                picked={picked}
                                style={{
                                    transform: `translateX(${offset * 118}px) translateY(${Math.abs(offset) * 6}px) rotate(${offset * 6}deg)`,
                                }}
                            >
                                <FolderTab picked={picked}>{p.name}</FolderTab>
                                <FolderLines>
                                    <div /><div /><div /><div /><div /><div />
                                </FolderLines>
                                {picked && <FolderStamp>Picked</FolderStamp>}
                            </Folder>
                        )
                    })}
                </Fan>
            </Stack>

            <Awaiting players={view.awaitingPlayers} />
        </TvStageArea>
    )
}

/* ══ Building answers ════════════════════════════════════════════════════ */

const DeskGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
`

/** One desk. The phone marks a finished player by removing them from the
 *  awaiting list; on a shared screen the finishing is the drama, so it's a
 *  stamp on paper — the same stamp the phone uses for a status. */
const Desk = styled.div<{ done?: boolean }>`
    position: relative;
    overflow: clip;
    border-radius: 8px;
    padding: 26px 28px;
    background: ${p => p.done ? paper.paper : paper.paperCool};
    box-shadow: ${p => p.done
        ? `0 0 0 2px ${paper.red}, 0 12px 24px -16px ${shade(0.4)}`
        : `0 0 0 1px ${shade(0.08)}, 0 12px 24px -16px ${shade(0.4)}`};
    transition: background 0.4s ease, box-shadow 0.4s ease;
    display: flex;
    align-items: center;
    gap: 16px;
    min-height: 124px;
`

const DeskName = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 27px;
    letter-spacing: -0.005em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
`

export function TvBuild({ view, players }: { view: WordPickerView; players: PlayerInfo[] }) {
    const seated = seatedOnly(players)
    const waitingIds = new Set(view.awaitingPlayers.map(p => p.id))
    const done = seated.length - view.awaitingPlayers.length

    return (
        <TvStageArea style={{ gap: 26, flex: 1 }}>
            <Prompt text={view.prompt} />

            <Row justify="space-between" align="baseline">
                <TvDisplay size={78}>
                    <MaskHeadline>Answers, please.</MaskHeadline>
                </TvDisplay>
                <TvDisplay size={64} style={{ color: paper.inkSoft }}>
                    {done}<span style={{ color: paper.inkMute }}> / {seated.length}</span>
                </TvDisplay>
            </Row>

            <DeskGrid style={{ margin: "auto 0" }}>
                {seated.map(p => {
                    const submitted = !waitingIds.has(p.id)
                    return (
                        <Desk key={p.id} done={submitted}>
                            <TvAvatar size={52}>{p.name.slice(0, 2)}</TvAvatar>
                            <DeskName>{p.name}</DeskName>
                            {submitted ? <TvStamp>Submitted</TvStamp> : <TvDots />}
                        </Desk>
                    )
                })}
            </DeskGrid>
        </TvStageArea>
    )
}

/* ══ Reveal ══════════════════════════════════════════════════════════════ */

const EntryCard = styled(TvPaper)`
    padding: 72px 84px 56px;
    max-width: 1480px;
    width: 100%;
    text-align: left;
`

const Pips = styled(Row)`
    margin-top: 18px;
`

const Pip = styled.div<{ seen?: boolean; current?: boolean }>`
    height: 6px;
    width: ${p => p.current ? 60 : 16}px;
    border-radius: 3px;
    background: ${p => p.seen ? paper.red : shade(0.18)};
    transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
`

const Byline = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 700;
    font-size: 34px;
    color: ${paper.inkSoft};
`

export function TvReveal({ view }: { view: ResultsView }) {
    const total = view.entries.length
    const idx = view.highlightedIndex
    const entry = idx >= 0 ? view.entries[idx] : null

    return (
        <Centered>
            {entry ? (
                <>
                    <TvEyebrow tone="mute">Entry {idx + 1} of {total}</TvEyebrow>
                    <Pips gap={8} justify="center">
                        {Array.from({ length: total }).map((_, i) => (
                            <Pip key={i} seen={i <= idx} current={i === idx} />
                        ))}
                    </Pips>
                    <EntryCard tiltLeft key={idx} style={{ marginTop: 30 }}>
                        <TvTape placement="tl" />
                        <MsQuoteWrap>
                            <TvQmark>&ldquo;</TvQmark>
                            <TvDisplay size={90} italic weight={700} style={{ lineHeight: 1.1 }}>
                                <MaskHeadline delay={0.2}>{entry.answer || "(no answer)"}</MaskHeadline>
                            </TvDisplay>
                        </MsQuoteWrap>
                        <TvRuleRed style={{ marginTop: 34 }} />
                        <Row justify="space-between" align="baseline" style={{ marginTop: 18, gap: 24 }}>
                            {entry.article
                                ? <TvDeck size={22}>from {entry.article.title}</TvDeck>
                                : <span />}
                            <Byline>— {entry.playerName}</Byline>
                        </Row>
                    </EntryCard>
                </>
            ) : (
                <EntryCard tiltLeft>
                    <TvTape placement="tl" />
                    <TvDisplay size={84} style={{ textAlign: "center" }}>
                        <MaskHeadline>{total} {total === 1 ? "entry" : "entries"} ready.</MaskHeadline>
                    </TvDisplay>
                </EntryCard>
            )}
            <TvDeck size={30} style={{ marginTop: 32 }}>{view.presenterName} is reading.</TvDeck>
        </Centered>
    )
}

/* ══ Vote ════════════════════════════════════════════════════════════════ */

const VoteGrid = styled.div<{ columns: number }>`
    display: grid;
    grid-template-columns: repeat(${p => p.columns}, 1fr);
    gap: 22px;
    min-height: 0;
`

/** The phone's entry card, unclickable. Same paper, same mark, same body face —
 *  a TV has nothing to tap, so it drops the pressed state and nothing else. */
const VoteCard = styled(TvPaper)`
    padding: 30px 30px 26px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-height: 200px;
    box-shadow: 0 0 0 1px ${shade(0.06)}, 0 14px 28px -14px ${shade(0.4)};
`

export function TvVote({ view, players }: { view: VotingView; players: PlayerInfo[] }) {
    const seated = seatedOnly(players)
    const cast = Math.max(0, seated.length - view.awaitingPlayers.length)
    const entries = Object.entries(view.entries)
    const columns = entries.length <= 3 ? Math.max(entries.length, 1) : entries.length <= 8 ? 4 : 5

    return (
        <TvStageArea style={{ gap: 26, flex: 1 }}>
            <Row justify="space-between" align="baseline">
                <TvDisplay size={88}>
                    <MaskHeadline>Pick the best misquote.</MaskHeadline>
                </TvDisplay>
                {/* A count, never a breakdown. Who is winning is exactly what a
                    room watching one screen must not know until voting closes. */}
                <TvDisplay size={64} style={{ color: paper.inkSoft }}>
                    {cast}<span style={{ color: paper.inkMute }}> / {seated.length}</span>
                </TvDisplay>
            </Row>

            <VoteGrid columns={columns}>
                {entries.map(([id, answer], i) => (
                    <VoteCard key={id} tiltLeft={i % 2 === 0} tiltRight={i % 2 === 1}>
                        <TvQmark>&ldquo;</TvQmark>
                        <TvQtext>{answer || "(no answer)"}</TvQtext>
                    </VoteCard>
                ))}
            </VoteGrid>

            <Awaiting players={view.awaitingPlayers} />
        </TvStageArea>
    )
}

/* ══ Winner ══════════════════════════════════════════════════════════════ */

const WinnerCard = styled(TvPaper)`
    padding: 60px 70px 50px;
    max-width: 1300px;
    width: 100%;
    text-align: left;
`

export function TvWinner({ view, players }: { view: VotingResultsView; players: PlayerInfo[] }) {
    const tallies = Object.entries(view.results)
    const max = tallies.length > 0 ? Math.max(...tallies.map(([, v]) => v)) : 0
    const winners = tallies.filter(([, v]) => v === max && max > 0)

    if (winners.length === 0) {
        return (
            <Centered>
                <Stack gap={22} align="center">
                    <TvStamp>No winner</TvStamp>
                    <TvDisplay size={120}>
                        <MaskHeadline>Nobody won this round.</MaskHeadline>
                    </TvDisplay>
                </Stack>
            </Centered>
        )
    }

    const [winnerId] = winners[0]
    const names = winners.map(([id]) => nameOf(players, id))

    return (
        <Centered>
            <Confetti count={60} />
            <Row justify="space-between" align="baseline" style={{ width: "100%", maxWidth: 1300 }}>
                <TvEyebrow tone="red">
                    {winners.length === 1 ? "Winning entry" : "Tied for the win"}
                </TvEyebrow>
                <TvEyebrow tone="mute">{max} {max === 1 ? "vote" : "votes"}</TvEyebrow>
            </Row>

            <WinnerCard tiltRight style={{ marginTop: 20 }}>
                <TvTape placement="tl" />
                <MsQuoteWrap>
                    <TvQmark>&ldquo;</TvQmark>
                    <TvDisplay size={82} italic weight={700} style={{ lineHeight: 1.16 }}>
                        <MaskHeadline delay={0.2}>{view.answers[winnerId] || "—"}</MaskHeadline>
                    </TvDisplay>
                </MsQuoteWrap>
                <TvRuleRed style={{ marginTop: 32 }} />
                <Row justify="space-between" align="baseline" style={{ marginTop: 18 }}>
                    <Byline>— {names.join(" & ")}</Byline>
                    <TvStamp>+{max} pt{max === 1 ? "" : "s"}</TvStamp>
                </Row>
            </WinnerCard>
        </Centered>
    )
}

/* ══ Standings ═══════════════════════════════════════════════════════════ */

const Race = styled(Stack)`
    margin-top: 8px;
`

const RaceRow = styled.div`
    display: grid;
    grid-template-columns: 74px 220px 1fr 130px;
    align-items: center;
    gap: 24px;
`

const RaceRank = styled.div<{ leader?: boolean }>`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-style: italic;
    font-size: 50px;
    line-height: 1;
    letter-spacing: -0.04em;
    font-variant-numeric: tabular-nums;
    color: ${p => p.leader ? paper.red : paper.inkMute};
`

const RaceName = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 34px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const Track = styled.div`
    position: relative;
    height: 42px;
    background: ${shade(0.06)};
    border-radius: 21px;
`

const Bar = styled.div<{ leader?: boolean }>`
    position: relative;
    height: 100%;
    border-radius: 21px;
    background: ${p => p.leader
        ? `linear-gradient(90deg, ${paper.red}, ${paper.redDeep})`
        : paper.ink};
    transform-origin: left;
    animation: tv-race 1.1s cubic-bezier(0.22, 1, 0.36, 1) both;

    @keyframes tv-race {
        from { transform: scaleX(0); }
        to { transform: scaleX(1); }
    }
`

const RaceScore = styled.div`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-size: 44px;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
    text-align: right;

    .gain {
        font-family: ${fonts.sans};
        font-weight: 700;
        font-size: 18px;
        color: ${paper.red};
        margin-left: 8px;
    }
`

const Credits = styled.div`
    margin-top: auto;
    padding-top: 14px;
    border-top: 1px dashed ${shade(0.18)};
`

export function TvStandings({ view, players, articles }: {
    view: RoundLeaderboardView
    players: PlayerInfo[]
    /** Titles used this round, carried over from the reveal — the server sends
     *  them with the entries, not with the standings. */
    articles: string[]
}) {
    const ranked = Object.entries(view.scores).sort((a, b) => b[1] - a[1])
    const top = ranked[0]?.[1] || 1

    return (
        <TvStageArea style={{ gap: 18, flex: 1 }}>
            <TvDisplay size={92}>
                <MaskHeadline>Current standings</MaskHeadline>
            </TvDisplay>

            <Race gap={14}>
                {ranked.map(([id, score], i) => {
                    const gained = view.roundResults[id] ?? 0
                    return (
                        <RaceRow key={id}>
                            <RaceRank leader={i === 0}>{pad2(i + 1)}</RaceRank>
                            <RaceName>{nameOf(players, id)}</RaceName>
                            <Track>
                                <Bar
                                    leader={i === 0}
                                    style={{
                                        width: `${Math.max(6, (score / top) * 100)}%`,
                                        animationDelay: `${0.2 + i * 0.08}s`,
                                    }}
                                />
                            </Track>
                            <RaceScore>
                                {score}
                                {gained > 0 && <span className="gain">+{gained}</span>}
                            </RaceScore>
                        </RaceRow>
                    )
                })}
            </Race>

            {articles.length > 0 && (
                <Credits>
                    <TvDeck size={20}>
                        {articles.join(" · ")} — Wikipedia, CC BY-SA
                    </TvDeck>
                </Credits>
            )}
        </TvStageArea>
    )
}

/* ══ Game over ═══════════════════════════════════════════════════════════ */

const Podium = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
    align-items: end;
    max-width: 1060px;
    margin: 20px auto 0;
    width: 100%;
`

const PodiumBlock = styled(Stack)`
    align-items: center;
`

const PodiumBar = styled.div<{ first?: boolean }>`
    width: 100%;
    background: ${p => p.first ? paper.red : paper.ink};
    color: ${paper.paper};
    border-radius: 8px 8px 0 0;
    margin-top: 14px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 18px;
    transform: scaleY(0);
    transform-origin: bottom;
    animation: tv-podium 0.8s cubic-bezier(0.34, 1.5, 0.5, 1) both;

    @keyframes tv-podium { to { transform: scaleY(1); } }
`

const PodiumRank = styled.div`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-size: 64px;
    line-height: 1;
    letter-spacing: -0.04em;
`

export interface BestEntry {
    answer: string
    author: string
    votes: number
}

export function TvGameOver({ view, players, best }: {
    view: GameOverView
    players: PlayerInfo[]
    best: BestEntry | null
}) {
    const ranked = Object.entries(view.finalScores).sort((a, b) => b[1] - a[1])
    if (ranked.length === 0) return <TvIntermission message="No scores to show." />

    const champion = ranked[0]
    // Silver, gold, bronze — the tallest block in the middle, as a podium reads.
    const order = [ranked[1], ranked[0], ranked[2]].filter(Boolean) as [string, number][]
    const heights = [190, 280, 140]
    const places = [2, 1, 3]

    return (
        <TvStageArea style={{ gap: 16, flex: 1, justifyContent: "center" }}>
            <Stack gap={6} align="center">
                <TvEyebrow tone="red">Champion</TvEyebrow>
                <TvDisplay size={140}>
                    <MaskHeadline>{nameOf(players, champion[0])}</MaskHeadline>
                </TvDisplay>
                <TvDeck size={32}>
                    {champion[1]} {champion[1] === 1 ? "point" : "points"}
                </TvDeck>
            </Stack>

            <Podium>
                {order.map(([id, score], i) => (
                    <PodiumBlock key={id} gap={4}>
                        <TvAvatar size={74}>{nameOf(players, id).slice(0, 2)}</TvAvatar>
                        <div style={{ fontFamily: fonts.serif, fontWeight: 800, fontSize: 36 }}>
                            {nameOf(players, id)}
                        </div>
                        <TvEyebrow tone="mute">{score} PTS</TvEyebrow>
                        <PodiumBar
                            first={places[i] === 1}
                            style={{ height: heights[i], animationDelay: `${0.4 + i * 0.15}s` }}
                        >
                            <PodiumRank>{places[i]}</PodiumRank>
                        </PodiumBar>
                    </PodiumBlock>
                ))}
            </Podium>

            {best && (
                <TvPlate drop style={{ maxWidth: 1200, margin: "18px auto 0", width: "100%" }}>
                    <MsEyebrow tone="sticky" style={{ fontSize: 14 }}>Best of the night</MsEyebrow>
                    <TvDisplay size={44} italic weight={700} style={{ color: paper.paper, marginTop: 10 }}>
                        &ldquo;{best.answer}&rdquo;
                    </TvDisplay>
                    <MsEyebrow tone="mute" style={{ fontSize: 14, marginTop: 10 }}>
                        {best.author} · {best.votes} {best.votes === 1 ? "vote" : "votes"}
                    </MsEyebrow>
                </TvPlate>
            )}
        </TvStageArea>
    )
}

/* ══ Stand by ════════════════════════════════════════════════════════════ */

export function TvIntermission({ message, detail, stamp = "Stand by" }: {
    message: string
    detail?: string
    stamp?: string
}) {
    return (
        <Centered>
            <Stack gap={26} align="center">
                <TvStamp>{stamp}</TvStamp>
                <TvDisplay size={120}>
                    <MaskHeadline>{message}</MaskHeadline>
                </TvDisplay>
                <TvDots />
                {detail && <TvEyebrow tone="mute">{detail}</TvEyebrow>}
            </Stack>
        </Centered>
    )
}
